// ─── Cockpit del Contratista ────────────────────────────────────────────────
// Módulo hermano de "Datos del Proyecto" y "Criterios y Justificación" que
// centraliza la documentación del contratista principal y sus subcontratistas
// durante la obra. Actúa como preámbulo estructurado a la certificación:
// la evidencia sube aquí y queda auto-vinculada a los 49 criterios BREEAM ES
// NC 2015 (o al esquema equivalente configurado en el expediente).
//
// FASE 1 · Fundamentos (esta iteración):
//   · Setup del expediente (esquema, nivel objetivo, EDMS, idiomas, pliegos)
//   · Alta de contratistas principal + subcontratistas
//   · Carga de pliegos PDF (metadata, sin extracción IA todavía)
//
// FASES SIGUIENTES (documentadas en INNOVACION_BREEAM/):
//   2 · Extracción IA de pliegos → checklist de requisitos automáticos
//   3 · Planes iniciales (PPCAI, PGR, GST 03, GUE)
//   4 · 5 flujos de seguimiento (materiales, consumos, operarios, residuos, transportes)
//   5 · Dashboard + régimen A-E
//   6 · Auto-vinculación con módulo de 49 criterios BREEAM
//   7 · Formaciones + actas
//   8 · Piloto AIRES ABAMA con pliegos MAD2

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
  HardHat, Folders, FileText, Users, GearSix,
  Plus, TrashSimple, FloppyDisk, ArrowRight,
  UploadSimple, Sparkle, CheckCircle, Warning, ArrowClockwise, Eye,
  ChartLineUp, MagnifyingGlass,
} from '@phosphor-icons/react'
import { PDFDocument } from 'pdf-lib'
import { supabase } from '../../lib/supabase'
import { callBreeamAnalyzer, parseAiJson } from '../../lib/breeamAnalyzer'
import PlanesIniciales from './PlanesIniciales'
import TrayectoriaCertificacion from './TrayectoriaCertificacion'
import Seguimiento from './Seguimiento'

// ── Chunking de PDFs · patrón del proyecto (breeam-analyzer) ───────────
// El proyecto ya tenía resuelto el problema de PDFs grandes: cada PDF
// se trocea en chunks de 20 páginas y la Edge Function los procesa por
// rondas acumulando el resultado (ver splitPdfIfNeeded en src/App.js).
// Adoptamos el MISMO patrón para no reinventar la rueda.
//
// 20 páginas por chunk es el balance probado en producción entre:
//   · límite de 100 páginas por documento de Anthropic
//   · límite de 200 000 tokens de contexto
//   · densidad típica de los documentos técnicos de construcción
const MAX_PDF_PAGES_PER_CHUNK = 20

// ── Paleta e estilos compartidos (idénticos a ProjectDetails) ──────────────
const C = {
  bg: '#FAF7F3', card: 'white', border: '#DDD5C8', text: '#3B2F2F',
  muted: '#7A6A5A', accent: '#1A3A4E', warn: '#D4614A', olive: '#8D9A2E',
  blueLight: '#EBF3F8', amber: '#C9A961',
}
const FF = "'DM Sans', sans-serif"
const LABEL_STYLE = { fontSize: '0.78rem', color: C.muted, fontWeight: '500', marginBottom: '0.25rem', display: 'block', fontFamily: FF }
const INPUT_STYLE = {
  width: '100%', padding: '0.45rem 0.6rem', fontSize: '0.82rem',
  border: `1px solid ${C.border}`, borderRadius: '4px', background: 'white',
  color: C.text, fontFamily: FF, outline: 'none',
}

// ── Opciones fijas configurables ────────────────────────────────────────────
const ESQUEMAS = [
  { code: 'BREEAM_ES_NC_2015',  label: 'BREEAM ES Nueva Construcción 2015' },
  { code: 'BREEAM_ES_IN_USE',   label: 'BREEAM ES In-Use' },
  { code: 'VERDE_NE_RES',       label: 'VERDE NE Residencial' },
  { code: 'DGNB_ES',            label: 'DGNB System ES' },
  { code: 'EDGE',               label: 'EDGE (IFC · Banco Mundial)' },
  { code: 'OTRO',               label: 'Otro esquema (indicar)' },
]

const NIVELES = [
  { code: 'PASS',        label: 'Pass · Bueno' },
  { code: 'GOOD',        label: 'Good · Bueno' },
  { code: 'VERY_GOOD',   label: 'Very Good · Muy Bueno' },
  { code: 'EXCELLENT',   label: 'Excellent · Excelente' },
  { code: 'OUTSTANDING', label: 'Outstanding · Excepcional' },
  { code: 'OTRO',        label: 'Otro nivel (indicar)' },
]

const FASES_PROYECTO = [
  { code: '',                    label: 'Deducir automáticamente por documentos' },
  { code: 'DISENO_BASICO',       label: 'Diseño Básico' },
  { code: 'DISENO_EJECUTIVO',    label: 'Diseño Ejecutivo' },
  { code: 'LICITACION',          label: 'Licitación' },
  { code: 'INICIO_OBRA',         label: 'Inicio de Obra' },
  { code: 'CIERRE_ESTRUCTURA',   label: 'Cierre de Estructura' },
  { code: 'INSTALACIONES',       label: 'Instalaciones' },
  { code: 'ENTREGA_OBRA',        label: 'Entrega de Obra' },
  { code: 'POST_CONSTRUCCION',   label: 'Post-construcción' },
]

const EDMS = [
  { code: 'ninguno',      label: 'Ninguno · gestión local' },
  { code: 'procore',      label: 'Procore' },
  { code: 'aconex',       label: 'Aconex (Oracle)' },
  { code: 'autodesk_cc',  label: 'Autodesk Construction Cloud' },
  { code: 'sharepoint',   label: 'SharePoint del cliente' },
  { code: 'google_drive', label: 'Google Drive del cliente' },
  { code: 'otro',         label: 'Otro (indicar)' },
]

const TIPOS_DOCUMENTO = [
  { code: 'memoria_basico',    label: 'Memoria de proyecto básico',   hint: 'Sistemas constructivos · materiales · certificaciones · residuos' },
  { code: 'memoria_ejecutivo', label: 'Memoria de proyecto ejecutivo', hint: 'Igual que básico pero con más detalle · sistemas ejecutivos' },
  { code: 'presupuesto',       label: 'Presupuesto de obra (MAT 3)',   hint: 'Partidas con materiales, cantidades y elemento BREEAM · alimenta MAT 3' },
  { code: 'presupuesto_capex', label: 'Presupuesto valorado (CapEx)',  hint: 'Presupuesto con precios · alimenta el módulo CapEx/OpEx (independiente de MAT 3)' },
  { code: 'pliego_breeam',     label: 'Pliego BREEAM',                 hint: 'Del asesor certificador del cliente · requisitos con cita literal' },
  { code: 'pliego_carbono',    label: 'Pliego de Huella de Carbono',   hint: '5 seguimientos + régimen A-E si aplica' },
  { code: 'scope_of_works',    label: 'Scope of Works del Employer',   hint: 'Proyectos internacionales · objetivo · EDMS · idiomas' },
  { code: 'planos',            label: 'Planos de proyecto',            hint: 'Extracción básica de materiales visibles' },
  { code: 'otro',              label: 'Otro documento',                hint: 'Extracción genérica de hallazgos BREEAM' },
]

const TIPO_LABELS = Object.fromEntries(TIPOS_DOCUMENTO.map(t => [t.code, t.label]))

const ESTADO_LABELS = {
  pendiente:  { label: 'Sin analizar', color: '#7A6A5A', bg: '#F5F0EA' },
  en_curso:   { label: 'Analizando…',  color: '#1A3A4E', bg: '#EBF3F8' },
  completado: { label: 'Analizado',    color: '#8D9A2E', bg: '#E8F1D8' },
  error:      { label: 'Error',        color: '#D4614A', bg: '#FDECE7' },
}

// ── Prompt por tipo de documento ────────────────────────────────────────
// Construye el prompt para Claude según el tipo del documento (memoria,
// presupuesto, pliego, scope of works, etc.) y añade contexto acumulado
// para rondas 2+ para que la IA no repita hallazgos ya extraídos.
function buildPromptDocumento(tipo, nombreDoc, rondaIndex, totalRondas, acumulado, subfase = null) {
  const isRondaSiguiente = rondaIndex > 0
  const contextoBlock = isRondaSiguiente
    ? `\n\nCONTEXTO ACUMULADO DE RONDAS ANTERIORES:\nYa se han procesado ${rondaIndex} de ${totalRondas} partes del documento.\n- ${acumulado.sistemas.length} sistemas constructivos ya identificados\n- ${acumulado.partidas.length} partidas presupuestarias ya extraídas\n- ${acumulado.hallazgos.length} hallazgos ya recogidos (temas: ${[...new Set(acumulado.hallazgos.map(h => h.tema))].join(', ') || 'ninguno'})\n\nAnaliza ESTA parte (${rondaIndex + 1}/${totalRondas}) y AÑADE solo elementos NUEVOS que no aparezcan ya en el acumulado.`
    : ''

  const cabecera = `Eres un asesor BREEAM ES Nueva Construccion 2015 senior con experiencia en el acompañamiento a contratistas. Analizas el documento adjunto y extraes información relevante para justificar los criterios BREEAM aplicables al contratista.

Documento: "${nombreDoc}"
Tipo: ${tipo}${contextoBlock}

REGLAS ESTRICTAS DE OUTPUT (importantes para que el JSON sea válido):
  1. Responde ÚNICAMENTE con un JSON válido. NO uses bloques markdown \`\`\`json\`\`\`. NO añadas texto antes ni después del JSON.
  2. Dentro de las citas literales, escapa las comillas dobles con \\" y los saltos de línea con \\n. Si la cita es larga, resúmela a la parte esencial (máx. 300 caracteres).
  3. NO uses comillas simples ' para delimitar strings, solo dobles ".
  4. Los campos numéricos van sin comillas (12, no "12"). Si un valor es desconocido, usa null.
  5. Nunca inventes datos que no aparezcan en el documento.`

  if (tipo === 'memoria_basico' || tipo === 'memoria_ejecutivo') {
    return `${cabecera}

TAREA: Extrae la información técnica en dos bloques:

BLOQUE 1 · "sistemas" (array): sistemas constructivos agrupados por categoría.
BLOQUE 2 · "hallazgos" (array): hallazgos puntuales fuera de sistemas.

Categorías válidas para sistemas:
  envolvente, estructura, particiones, acabados, instalaciones_ac,
  instalaciones_electricas, instalaciones_agua, instalaciones_transporte, otros

Temas válidos para hallazgos:
  materiales, residuos, agua, energia, biodiversidad, acustica, aire_interior, transporte, gestion, otros

Formato JSON:
{
  "resumen": "Descripción breve (2-3 frases)",
  "sistemas": [
    {"categoria":"envolvente","descripcion":"...","cita_literal":"...","pagina":15,
     "materiales_mencionados":["..."],"certificaciones_mencionadas":["FSC"],
     "criterios_breeam_relacionados":["MAT 03"]}
  ],
  "hallazgos": [
    {"tema":"residuos","cita_literal":"...","pagina":42,"hallazgo":"...","criterios_breeam_relacionados":["RSD 01"],"confianza":"alta"}
  ]
}`
  }

  if (tipo === 'presupuesto') {
    const subfaseBlock = subfase
      ? `\nSUBFASE DEL DOCUMENTO: "${subfase}". Todas las partidas extraídas pertenecen a esta subfase. Guarda "${subfase}" en el campo "subfase" de cada partida. NO mezcles con otras subfases aunque encuentres referencias cruzadas.`
      : `\nSUBFASE DEL DOCUMENTO: no aplica (proyecto único). Usa "subfase": null en cada partida.`
    return `${cabecera}${subfaseBlock}

TAREA: Extrae partidas presupuestarias con sus materiales, cantidades e importes económicos.

INSTRUCCIONES:
  - Cada material individualmente, NO agrupes
  - Ignora mano de obra, gastos generales, honorarios (a menos que estén integrados en el importe de la partida)
  - Extrae código de partida si aparece
  - Si no hay cantidad clara usa null
  - **IMPORTANTE — PRECIOS**: extrae el precio unitario (€/unidad) y el importe total (€) de cada partida cuando aparezcan en el documento. Si no aparece explícitamente, usa null (nunca inventes cifras). Los precios suelen aparecer al final de la fila de cada partida.
  - nombre_busqueda_epd: nombre técnico en inglés simplificado

Categorías válidas para elemento_breeam:
  cimientos, estructura, losa_pb, forjados, fachadas, cubiertas, particiones, acondicionamientos, paisaje

Formato JSON (nota los nuevos campos "subfase", "precio_unitario_eur" e "importe_total_eur"):
{
  "resumen": "Descripción breve (2-3 frases)",
  "partidas": [
    {"codigo":"01.02.003","nombre_partida":"...","pagina":12,"subfase":${subfase ? `"${subfase}"` : 'null'},
     "materiales":[{"nombre":"...","nombre_busqueda_epd":"...","cantidad":150.5,"unidad":"m2","elemento_breeam":"cubiertas","fabricante_sugerido":null,"precio_unitario_eur":45.20,"importe_total_eur":6803.10,"notas":null}]}
  ],
  "hallazgos": [
    {"tema":"certificaciones","cita_literal":"...","pagina":5,"hallazgo":"...","criterios_breeam_relacionados":["MAT 03"],"confianza":"alta"}
  ]
}`
  }

  if (tipo === 'presupuesto_capex') {
    const subfaseBlock = subfase
      ? `\nSUBFASE DEL DOCUMENTO: "${subfase}". Todas las partidas extraídas pertenecen a esta subfase. Guarda "${subfase}" en el campo "subfase" de cada partida.`
      : `\nSUBFASE DEL DOCUMENTO: proyecto único. Usa "subfase": null en cada partida.`
    return `${cabecera}${subfaseBlock}

TAREA: Extrae partidas presupuestarias con importes económicos para análisis financiero CapEx/OpEx.

**Este documento NO alimenta MAT 3**. Solo se usa para el análisis financiero del proyecto.

INSTRUCCIONES:
  - Extrae cada partida presupuestaria de forma independiente (no agrupes materiales por partida)
  - Incluye mano de obra, gastos generales, equipos si tienen precio
  - Extrae SIEMPRE el precio unitario (€/unidad) y el importe total (€) de cada partida
  - Si un dato no aparece explícitamente, usa null — NO inventes cifras
  - Los precios suelen aparecer al final de la fila (columnas "Precio", "Importe", "Total")

CLASIFICACIÓN por partida (dos dimensiones):

DIMENSIÓN 1 · CAPÍTULO CONSTRUCTIVO (código CTE estándar):
  01_movimiento_tierras · 02_cimentacion · 03_estructura · 04_cubiertas ·
  05_cerramientos · 06_particiones · 07_acabados · 08_instalaciones ·
  09_urbanizacion · 10_seguridad · 11_control_calidad · 12_gestion_residuos ·
  13_otros

DIMENSIÓN 2 · SISTEMA / SUBSISTEMA:
  Estructura · Envolvente · Cubierta · Fachada · Particiones · Acabados ·
  Climatizacion · ACS · Ventilacion · Fontaneria · Saneamiento · Iluminacion ·
  Baja_Tension · Media_Tension · Domotica · Fotovoltaica · Ascensores ·
  Proteccion_Contra_Incendios · Urbanizacion · Riego · Aguas_Grises · Aguas_Pluviales ·
  Gestion_Residuos · Seguridad_Salud · Otros

═══════════════════════════════════════════════════════════════════════════
CLASIFICACIÓN SOSTENIBILIDAD · CRITERIOS ESTRICTOS
═══════════════════════════════════════════════════════════════════════════

Marca es_sostenible=true SOLO si la descripción cita EXPLÍCITAMENTE al menos uno
de los siguientes criterios objetivos. Sin cita literal → es_sostenible=false.

CATEGORÍAS VÁLIDAS (usa el código exacto en el campo criterio_sostenibilidad):

  1) certificacion_ambiental — la descripción menciona explícitamente:
     "FSC C###", "PEFC ####", "DAP", "EPD", "BES 6001", "ISO 14001",
     "Cradle to Cradle", "Ecolabel UE", "A+ VOC", "Emicode EC1"

  2) energia_renovable — sistema de producción/aprovechamiento renovable in situ:
     fotovoltaica, solar térmica, aerotermia, geotermia, biomasa, minieólica

  3) alta_eficiencia_energetica — sistema con eficiencia verificable superior al CTE:
     - LED con control DALI/KNX/DIN 66277
     - Motor IE4/IE5, VSD, VFD
     - BMS / SGE / EMS ISO 50001
     - Recuperación de calor con eficiencia >70%

  4) eficiencia_hidrica — reducción cuantificable vs baseline:
     - Grifería <6 L/min citado
     - Inodoro dual flush 3/6 L
     - Recuperación aguas grises / pluviales / SUDS
     - Riego por goteo con sensor de humedad
     - Xerojardinería (especies autóctonas, bajo consumo)

  5) mejora_termica_envolvente — dato numérico mejor que CTE-HE1:
     - Vidrio con U ≤ 1.4 W/m²K y g ≤ 0.35 CITADO en la descripción
     - Aislamiento con λ ≤ 0.030 W/mK CITADO
     - Fachada ventilada / SATE con espesor > 12 cm
     - Envolvente NZEB

  6) material_bajo_carbono — con evidencia:
     - Hormigón con CEM III o CEM V (bajo carbono) CITADO
     - Cemento con >30% adiciones (fillers, cenizas volantes, GGBS)
     - Madera FSC/PEFC / madera laminada certificada
     - Acero reciclado con % citado
     - Cerámica reciclada, hormigón reciclado

  7) movilidad_sostenible — infraestructura verificable:
     - Puntos de carga VE
     - Aparcabicis con capacidad citada
     - Vestuarios/duchas para ciclistas

  8) biodiversidad — con evidencia:
     - Cubierta vegetal / verde
     - Zonas verdes con especies autóctonas
     - Hotel de insectos / refugios de fauna

═══════════════════════════════════════════════════════════════════════════
LISTA NEGRA · PATRONES QUE **NO** SON SOSTENIBLES (aunque suenen bien)
═══════════════════════════════════════════════════════════════════════════

Marca es_sostenible=false para todo lo siguiente aunque la descripción incluya
palabras positivas:

  ❌ Aditivos hidrofugantes en hormigón (Sika Plastocrete-N, similares) →
     es prescripción técnica normal para cimentaciones y humedad, NO sostenibilidad
  ❌ Cementos CEM I o CEM II sin evidencia de bajo carbono
  ❌ Madera tropical (IROKO, teca, ipé) SIN certificación FSC/PEFC citada
  ❌ Enfoscados / morteros "hidrofugados" o "duraderos" convencionales
  ❌ Gestión de residuos de construcción (RCD) → es OBLIGATORIO por RD 105/2008
  ❌ Placas de yeso "resistentes a humedad" tipo Diamant, DFH → estándar en baños
  ❌ Persianas motorizadas o cortinas sin dato de coeficiente solar
  ❌ Ascensores sin cuarto de máquinas → configuración estándar sin dato eficiencia
  ❌ Barandillas galvanizadas, pinturas antioxidantes, sellantes → convencional
  ❌ Pavimentos hormigón fratasados con "macrofibras" o "endurecedores"
  ❌ Vidrios sin U/g citados (solo "de altas prestaciones" no basta)
  ❌ Cualquier partida donde la sostenibilidad requiere INFERIR o INTERPRETAR

**Regla de oro**: si tienes que "asumir" o "inferir" que es sostenible → es false.
Si la descripción cita literalmente una certificación o un valor técnico verificable → true.

═══════════════════════════════════════════════════════════════════════════

Formato JSON:
{
  "resumen": "Descripción del presupuesto (2-3 frases)",
  "partidas": [
    {
      "codigo": "02.02.01",
      "descripcion": "Hormigón de limpieza HL150/B/20, 10 cm espesor medio, en base de cimentaciones",
      "cantidad": 864.51,
      "unidad": "m2",
      "precio_unitario_eur": 8.75,
      "importe_total_eur": 7564.46,
      "capitulo_constructivo": "02_cimentacion",
      "sistema": "Estructura",
      "es_sostenible": false,
      "criterio_sostenibilidad": null,
      "evidencia_literal": null,
      "descripcion_sostenibilidad": null,
      "subfase": ${subfase ? `"${subfase}"` : 'null'},
      "pagina": 12
    },
    {
      "codigo": "02.06.14",
      "descripcion": "Puertas Cortizo COR VISION con vidrio SUPERSOLAR 60/27 · U=1.2 W/m²K · g=0.27 · cámara THERMIX",
      "cantidad": 12,
      "unidad": "ud",
      "precio_unitario_eur": 3542.00,
      "importe_total_eur": 42504.00,
      "capitulo_constructivo": "05_cerramientos",
      "sistema": "Fachada",
      "es_sostenible": true,
      "criterio_sostenibilidad": "mejora_termica_envolvente",
      "evidencia_literal": "U=1.2 W/m²K · g=0.27",
      "descripcion_sostenibilidad": "Vidrio con U y g mejores que CTE-HE1 mínimo",
      "subfase": ${subfase ? `"${subfase}"` : 'null'},
      "pagina": 47
    }
  ],
  "hallazgos": [
    {"tema":"certificaciones","cita_literal":"...","pagina":5,"hallazgo":"...","criterios_breeam_relacionados":["MAT 03"],"confianza":"alta"}
  ]
}`
  }

  if (tipo === 'pliego_breeam') {
    return `${cabecera}

TAREA: Extrae los requisitos del pliego BREEAM asignados al contratista.

Formato JSON:
{
  "resumen": "Resumen del pliego (2-3 frases)",
  "hallazgos": [
    {"tema":"requisito_pliego","criterio_breeam":"GST 02","requisito":"...","cita_literal":"...","pagina":12,"puntos":2,"frecuencia":"semanal","confianza":"alta"}
  ]
}`
  }

  if (tipo === 'pliego_carbono') {
    return `${cabecera}

TAREA: Extrae los 5 seguimientos del pliego de carbono y régimen A-E si aplica.

Formato JSON:
{
  "resumen": "Resumen del pliego (2-3 frases)",
  "hallazgos": [
    {"tema":"seguimiento_materiales","cita_literal":"...","pagina":15,"hallazgo":"...","frecuencia":"diaria","criterios_breeam_relacionados":[],"confianza":"alta"}
  ]
}`
  }

  if (tipo === 'scope_of_works') {
    return `${cabecera}

TAREA: Extrae objetivos del Employer, EDMS, idiomas y restricciones.

Formato JSON:
{
  "resumen": "Resumen del SoW (2-3 frases)",
  "hallazgos": [
    {"tema":"objetivo_certificacion","cita_literal":"...","pagina":3,"hallazgo":"...","criterios_breeam_relacionados":[],"confianza":"alta"}
  ]
}`
  }

  return `${cabecera}

TAREA: Extracción genérica de hallazgos relevantes para BREEAM.

Formato JSON:
{
  "resumen": "Descripción breve (1-2 frases)",
  "hallazgos": [
    {"tema":"materiales","cita_literal":"...","pagina":null,"hallazgo":"...","criterios_breeam_relacionados":[],"confianza":"alta"}
  ]
}`
}

// ── Sub-componentes helpers ─────────────────────────────────────────────────
function Field({ label, children, hint, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}`, minWidth: 0 }}>
      <label style={LABEL_STYLE}>{label}</label>
      {children}
      {hint && (
        <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: '0.25rem', fontStyle: 'italic', fontFamily: FF }}>
          {hint}
        </div>
      )}
    </div>
  )
}

function TextInput({ value, onChange, type = 'text', placeholder }) {
  return (
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} style={INPUT_STYLE} />
  )
}

function SelectInput({ value, onChange, options, placeholder = '— Selecciona —' }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={INPUT_STYLE}>
      <option value="">{placeholder}</option>
      {options.map(opt => (
        <option key={opt.code || opt} value={opt.code || opt}>
          {opt.label || opt}
        </option>
      ))}
    </select>
  )
}

function Button({ children, onClick, variant = 'primary', icon: Icon, disabled = false, small = false }) {
  const bg = variant === 'primary' ? C.accent : variant === 'danger' ? C.warn : variant === 'olive' ? C.olive : 'white'
  const fg = variant === 'ghost' ? C.text : 'white'
  const border = variant === 'ghost' ? `1px solid ${C.border}` : 'none'
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        padding: small ? '0.35rem 0.75rem' : '0.55rem 1rem',
        fontSize: small ? '0.78rem' : '0.86rem',
        background: bg, color: fg, border, borderRadius: '4px',
        fontFamily: FF, fontWeight: '600', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
      }}>
      {Icon && <Icon size={small ? 14 : 16} />}
      {children}
    </button>
  )
}

function Card({ title, icon: Icon, children, action }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '6px', marginBottom: '1rem' }}>
      <div style={{ padding: '0.875rem 1rem', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {Icon && <Icon size={16} color={C.accent} />}
        <span style={{ fontFamily: FF, fontWeight: '600', color: C.text, fontSize: '0.92rem', flex: 1 }}>
          {title}
        </span>
        {action}
      </div>
      <div style={{ padding: '1rem' }}>{children}</div>
    </div>
  )
}

function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem', color: C.muted, fontFamily: FF }}>
      {Icon && <Icon size={40} color={C.border} style={{ marginBottom: '0.5rem' }} />}
      <div style={{ fontSize: '0.9rem', fontWeight: '600', color: C.text, marginBottom: '0.25rem' }}>{title}</div>
      {hint && <div style={{ fontSize: '0.8rem', marginBottom: '0.75rem' }}>{hint}</div>}
      {action}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//   TAB 1 · SETUP DEL EXPEDIENTE
// ═══════════════════════════════════════════════════════════════════════════
function SetupExpediente({ expediente, onSave, saving }) {
  const [form, setForm] = useState(expediente || {
    esquema: 'BREEAM_ES_NC_2015',
    nivel_objetivo: 'VERY_GOOD',
    fase_actual: null,
    edms_destino: 'ninguno',
    idiomas_entrega: ['es'],
    tiene_pliego_carbono: false,
    tiene_regimen_niveles: false,
    fecha_inicio_obra: '',
    duracion_meses: '',
    notas_expediente: '',
  })

  useEffect(() => { if (expediente) setForm(expediente) }, [expediente])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const toggleIdioma = (code) => {
    const cur = form.idiomas_entrega || ['es']
    const nuevo = cur.includes(code) ? cur.filter(x => x !== code) : [...cur, code]
    set('idiomas_entrega', nuevo.length ? nuevo : ['es'])
  }

  return (
    <Card title="Configuración del expediente" icon={GearSix}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem 1rem' }}>
        <Field label="Esquema de certificación" span={1}>
          <SelectInput value={form.esquema} onChange={v => set('esquema', v)} options={ESQUEMAS} />
        </Field>
        {form.esquema === 'OTRO' && (
          <Field label="Otro esquema (nombre)" span={1}>
            <TextInput value={form.esquema_otro} onChange={v => set('esquema_otro', v)} />
          </Field>
        )}
        <Field label="Nivel objetivo" span={1}>
          <SelectInput value={form.nivel_objetivo} onChange={v => set('nivel_objetivo', v)} options={NIVELES} />
        </Field>
        {form.nivel_objetivo === 'OTRO' && (
          <Field label="Otro nivel (nombre)" span={1}>
            <TextInput value={form.nivel_objetivo_otro} onChange={v => set('nivel_objetivo_otro', v)} />
          </Field>
        )}
        <Field label="Fase actual del proyecto" span={1} hint="Se usa como posición en el eje X de la Trayectoria. Si dejas 'Deducir automáticamente', se calcula por los tipos de documento subidos.">
          <SelectInput value={form.fase_actual || ''} onChange={v => set('fase_actual', v || null)} options={FASES_PROYECTO} />
        </Field>
        <Field label="EDMS del cliente (destino de entrega)" span={1}>
          <SelectInput value={form.edms_destino} onChange={v => set('edms_destino', v)} options={EDMS} />
        </Field>
        <Field label="URL del EDMS (opcional)" span={1}>
          <TextInput value={form.edms_url} onChange={v => set('edms_url', v)} placeholder="https://..." />
        </Field>

        <Field label="Idiomas de entrega" span={2} hint="La app generará las plantillas en los idiomas seleccionados. Los informes internos se mantienen siempre en español.">
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {[
              { code: 'es', label: 'Español' },
              { code: 'en', label: 'English' },
              { code: 'fr', label: 'Français' },
              { code: 'de', label: 'Deutsch' },
            ].map(l => {
              const active = (form.idiomas_entrega || []).includes(l.code)
              return (
                <button key={l.code} onClick={() => toggleIdioma(l.code)}
                  style={{
                    padding: '0.4rem 0.9rem', fontSize: '0.78rem', fontFamily: FF,
                    border: `1px solid ${active ? C.accent : C.border}`, borderRadius: '20px',
                    background: active ? C.accent : 'white',
                    color: active ? 'white' : C.text, cursor: 'pointer',
                    fontWeight: active ? '600' : '400',
                  }}>
                  {l.label}
                </button>
              )
            })}
          </div>
        </Field>

        <Field label="¿Aplica Pliego de Huella de Carbono?" span={1} hint="Habilita los 5 seguimientos operativos y el motor de cálculo ISO 14067/68.">
          <SelectInput value={form.tiene_pliego_carbono ? 'si' : 'no'}
            onChange={v => set('tiene_pliego_carbono', v === 'si')}
            options={[{code:'no',label:'No'},{code:'si',label:'Sí'}]} />
        </Field>
        <Field label="¿Régimen de niveles de servicio A-E?" span={1} hint="Activa el motor que evalúa mensualmente el nivel Correcto · Aceptable · Degradado · Muy Degradado · No Aceptable.">
          <SelectInput value={form.tiene_regimen_niveles ? 'si' : 'no'}
            onChange={v => set('tiene_regimen_niveles', v === 'si')}
            options={[{code:'no',label:'No'},{code:'si',label:'Sí'}]} />
        </Field>

        <Field label="Fecha inicio obra" span={1}>
          <TextInput type="date" value={form.fecha_inicio_obra} onChange={v => set('fecha_inicio_obra', v)} />
        </Field>
        <Field label="Duración prevista (meses)" span={1}>
          <TextInput type="number" value={form.duracion_meses} onChange={v => set('duracion_meses', v)} placeholder="24" />
        </Field>

        <Field label="Notas del expediente" span={2}>
          <textarea value={form.notas_expediente || ''} onChange={e => set('notas_expediente', e.target.value)}
            rows={3} style={{ ...INPUT_STYLE, resize: 'vertical', fontFamily: FF }} />
        </Field>
      </div>

      <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <Button icon={FloppyDisk} onClick={() => onSave(form)} disabled={saving}>
          {saving ? 'Guardando...' : (expediente?.id ? 'Actualizar expediente' : 'Crear expediente')}
        </Button>
      </div>
    </Card>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//   TAB 2 · CONTRATISTAS
// ═══════════════════════════════════════════════════════════════════════════
function TabContratistas({ contratistas, onAdd, onDelete, onUpdate, expedienteId }) {
  const [nuevo, setNuevo] = useState({ rol: 'principal', nombre: '', cif: '', especialidad: '',
    contacto_nombre: '', contacto_email: '', contacto_tel: '' })
  const [showForm, setShowForm] = useState(false)

  const guardar = () => {
    if (!nuevo.nombre.trim()) return alert('Introduce el nombre / razón social del contratista.')
    onAdd(nuevo)
    setNuevo({ rol: 'principal', nombre: '', cif: '', especialidad: '',
      contacto_nombre: '', contacto_email: '', contacto_tel: '' })
    setShowForm(false)
  }

  if (!expedienteId) {
    return (
      <Card title="Contratistas del proyecto" icon={Users}>
        <EmptyState icon={GearSix} title="Configura primero el expediente"
          hint="Antes de dar de alta contratistas, define el esquema y nivel objetivo en la pestaña Setup." />
      </Card>
    )
  }

  const principal = contratistas.find(c => c.rol === 'principal')
  const subs = contratistas.filter(c => c.rol === 'subcontratista')

  return (
    <>
      <Card title="Contratista principal" icon={HardHat}
        action={!principal && (
          <Button small icon={Plus} onClick={() => { setNuevo(n => ({ ...n, rol: 'principal' })); setShowForm(true) }}>
            Añadir
          </Button>
        )}>
        {principal ? (
          <ContratistaRow c={principal} onDelete={onDelete} onUpdate={onUpdate} />
        ) : (
          <EmptyState icon={HardHat} title="Sin contratista principal"
            hint="Añade el contratista principal del proyecto (ejemplo: Grupo SEMI, Constructor X)." />
        )}
      </Card>

      <Card title={`Subcontratistas (${subs.length})`} icon={Users}
        action={
          <Button small icon={Plus} onClick={() => { setNuevo(n => ({ ...n, rol: 'subcontratista' })); setShowForm(true) }}>
            Añadir subcontratista
          </Button>
        }>
        {subs.length === 0 ? (
          <EmptyState icon={Users} title="Sin subcontratistas"
            hint="Los subcontratistas heredan la trazabilidad documental del contratista principal (albaranes, EPDs, listados firmados)." />
        ) : (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {subs.map(c => <ContratistaRow key={c.id} c={c} onDelete={onDelete} onUpdate={onUpdate} />)}
          </div>
        )}
      </Card>

      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(59,47,47,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }} onClick={e => { if (e.target === e.currentTarget) setShowForm(false) }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', width: '90%', maxWidth: '520px' }}>
            <h3 style={{ margin: '0 0 1rem 0', fontFamily: FF, color: C.text, fontSize: '1rem' }}>
              {nuevo.rol === 'principal' ? 'Nuevo contratista principal' : 'Nuevo subcontratista'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <Field label="Razón social" span={2}>
                <TextInput value={nuevo.nombre} onChange={v => setNuevo(n => ({ ...n, nombre: v }))} />
              </Field>
              <Field label="CIF">
                <TextInput value={nuevo.cif} onChange={v => setNuevo(n => ({ ...n, cif: v }))} />
              </Field>
              {nuevo.rol === 'subcontratista' && (
                <Field label="Especialidad">
                  <TextInput value={nuevo.especialidad} onChange={v => setNuevo(n => ({ ...n, especialidad: v }))}
                    placeholder="Estructura · MEP · Cubierta..." />
                </Field>
              )}
              <Field label="Contacto · nombre" span={2}>
                <TextInput value={nuevo.contacto_nombre} onChange={v => setNuevo(n => ({ ...n, contacto_nombre: v }))} />
              </Field>
              <Field label="Email">
                <TextInput type="email" value={nuevo.contacto_email} onChange={v => setNuevo(n => ({ ...n, contacto_email: v }))} />
              </Field>
              <Field label="Teléfono">
                <TextInput value={nuevo.contacto_tel} onChange={v => setNuevo(n => ({ ...n, contacto_tel: v }))} />
              </Field>
            </div>
            <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <Button variant="ghost" small onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button small icon={FloppyDisk} onClick={guardar}>Guardar</Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function ContratistaRow({ c, onDelete, onUpdate }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      padding: '0.6rem 0.75rem', border: `1px solid ${C.border}`, borderRadius: '4px', background: '#FDFCF9',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: FF, fontWeight: '600', color: C.text, fontSize: '0.9rem' }}>
          {c.nombre}
          {c.especialidad && (
            <span style={{ fontWeight: '400', color: C.muted, fontSize: '0.78rem', marginLeft: '0.5rem' }}>
              · {c.especialidad}
            </span>
          )}
        </div>
        <div style={{ fontSize: '0.75rem', color: C.muted, fontFamily: FF, marginTop: '0.15rem' }}>
          {c.cif && <>CIF {c.cif} · </>}
          {c.contacto_nombre && <>{c.contacto_nombre} · </>}
          {c.contacto_email || c.contacto_tel || 'Sin datos de contacto'}
        </div>
      </div>
      <Button variant="ghost" small icon={TrashSimple}
        onClick={() => { if (window.confirm(`¿Eliminar «${c.nombre}»?`)) onDelete(c.id) }}>
        Eliminar
      </Button>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//   TAB 3 · DOCUMENTACIÓN DEL PROYECTO · subida PDF + IA
// ═══════════════════════════════════════════════════════════════════════════
const SUBFASES_OPTIONS = [
  { code: '',   label: 'Sin subfase (proyecto único)' },
  { code: 'A2', label: 'A2 · Subfase A2' },
  { code: 'A3', label: 'A3 · Subfase A3' },
  { code: 'A4', label: 'A4 · Subfase A4' },
  { code: 'B',  label: 'B · Fase B' },
  { code: 'C',  label: 'C · Fase C' },
]

function TabDocumentos({ documentos, onAdd, onAddDesdePliego, onDelete, onAnalyze, onViewDetail, expedienteId, projectId, userId }) {
  const [form, setForm] = useState({ tipo: 'memoria_ejecutivo', nombre: '', autor_asesor: '', fecha_documento: '', subfase: '' })
  const [file, setFile] = useState(null)
  const [filePages, setFilePages] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [showPicker, setShowPicker] = useState(false)
  const fileInputRef = useRef(null)

  const onSelectFile = async (f) => {
    setFile(f)
    setFilePages(null)
    if (!f) return
    try {
      const buf = await f.arrayBuffer()
      const pdf = await PDFDocument.load(buf)
      setFilePages(pdf.getPageCount())
    } catch (e) {
      console.warn('No se pudo contar páginas:', e.message)
    }
  }

  if (!expedienteId) {
    return (
      <Card title="Documentación del proyecto" icon={FileText}>
        <EmptyState icon={GearSix} title="Configura primero el expediente"
          hint="Los documentos se asocian al expediente. Créalo en la pestaña Setup." />
      </Card>
    )
  }

  const reset = () => {
    setForm({ tipo: 'memoria_ejecutivo', nombre: '', autor_asesor: '', fecha_documento: '', subfase: '' })
    setFile(null)
    setFilePages(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const guardar = async () => {
    if (!form.nombre.trim()) return alert('Introduce el nombre del documento.')
    if (!file) return alert('Selecciona el PDF del documento.')
    setUploading(true)
    try {
      // 1. Cargar el PDF y contar páginas
      const originalArrayBuf = await file.arrayBuffer()
      const originalPdf = await PDFDocument.load(originalArrayBuf, { ignoreEncryption: true })
      const pageCount = originalPdf.getPageCount()

      if (pageCount <= MAX_PDF_PAGES_PER_CHUNK) {
        // 2A. Documento pequeño · subida directa a storage_path
        const uid = crypto.randomUUID()
        const path = `${userId}/${projectId}/${uid}.pdf`
        const { error: upErr } = await supabase.storage
          .from('cc-documentos-proyecto')
          .upload(path, file, { contentType: 'application/pdf', upsert: false })
        if (upErr) throw new Error(`Error al subir el PDF · ${upErr.message}`)

        await onAdd({
          ...form,
          storage_path: path,
          chunks: null,
          archivo_bytes: file.size,
          archivo_paginas: pageCount,
          analisis_estado: 'pendiente',
        })
      } else {
        // 2B. Documento grande · chunking en el frontend + array de chunks
        const numChunks = Math.ceil(pageCount / MAX_PDF_PAGES_PER_CHUNK)
        const chunks = []
        for (let i = 0; i < numChunks; i++) {
          const inicio = i * MAX_PDF_PAGES_PER_CHUNK
          const fin = Math.min(inicio + MAX_PDF_PAGES_PER_CHUNK, pageCount)
          const indices = []
          for (let p = inicio; p < fin; p++) indices.push(p)

          const chunkPdf = await PDFDocument.create()
          const copied = await chunkPdf.copyPages(originalPdf, indices)
          copied.forEach(p => chunkPdf.addPage(p))
          const chunkBytes = await chunkPdf.save()

          const uid = crypto.randomUUID()
          const path = `${userId}/${projectId}/${uid}.pdf`
          const chunkBlob = new Blob([chunkBytes], { type: 'application/pdf' })
          const { error: upErr } = await supabase.storage
            .from('cc-documentos-proyecto')
            .upload(path, chunkBlob, { contentType: 'application/pdf', upsert: false })
          if (upErr) throw new Error(`Error al subir la parte ${i + 1}/${numChunks} · ${upErr.message}`)

          chunks.push({
            storage_path: path,
            indice: i,
            paginas: fin - inicio,
            bytes: chunkBlob.size,
            pagina_inicio: inicio + 1,
            pagina_fin: fin,
          })
        }

        // Un solo registro en BD con el array de chunks
        await onAdd({
          ...form,
          storage_path: null,
          chunks,
          archivo_bytes: file.size,
          archivo_paginas: pageCount,
          analisis_estado: 'pendiente',
        })
      }

      reset()
      setShowForm(false)
    } catch (e) {
      alert(e.message)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Card title={`Documentación del proyecto (${documentos.length})`} icon={FileText}
      action={
        <div style={{ display: 'inline-flex', gap: '0.4rem' }}>
          <Button small variant="ghost" icon={FileText} onClick={() => setShowPicker(true)}>
            Elegir del pliego
          </Button>
          <Button small icon={Plus} onClick={() => setShowForm(true)}>
            Añadir documento
          </Button>
        </div>
      }>
      {documentos.length === 0 ? (
        <EmptyState icon={FileText} title="Sin documentos cargados"
          hint="Sube la memoria del proyecto, el presupuesto o cualquier otro documento técnico. La IA extraerá automáticamente sistemas, materiales, partidas y hallazgos relevantes para los criterios BREEAM." />
      ) : (
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {documentos.map(d => (
            <DocumentoRow key={d.id} d={d}
              onDelete={onDelete} onAnalyze={onAnalyze} onView={onViewDetail} />
          ))}
        </div>
      )}

      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(59,47,47,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }} onClick={e => { if (e.target === e.currentTarget) { reset(); setShowForm(false) } }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', width: '90%', maxWidth: '560px' }}>
            <h3 style={{ margin: '0 0 1rem 0', fontFamily: FF, color: C.text, fontSize: '1rem' }}>
              Nuevo documento
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <Field label="Tipo de documento" span={2}
                hint={TIPOS_DOCUMENTO.find(t => t.code === form.tipo)?.hint}>
                <SelectInput value={form.tipo} onChange={v => setForm(f => ({ ...f, tipo: v }))} options={TIPOS_DOCUMENTO} />
              </Field>
              <Field label="Nombre visible" span={2}>
                <TextInput value={form.nombre} onChange={v => setForm(f => ({ ...f, nombre: v }))}
                  placeholder="Memoria ejecutiva · Arquitectura XYZ" />
              </Field>
              <Field label="Autor / redactor">
                <TextInput value={form.autor_asesor} onChange={v => setForm(f => ({ ...f, autor_asesor: v }))}
                  placeholder="Estudio XYZ, PI Data Tech..." />
              </Field>
              <Field label="Fecha del documento">
                <TextInput type="date" value={form.fecha_documento}
                  onChange={v => setForm(f => ({ ...f, fecha_documento: v }))} />
              </Field>
              <Field label="Subfase del proyecto" span={2}
                hint="Si el proyecto tiene fases separadas (A2, A3…), selecciónala aquí. Todas las partidas extraídas del PDF quedarán etiquetadas con esta subfase.">
                <SelectInput value={form.subfase || ''} onChange={v => setForm(f => ({ ...f, subfase: v }))} options={SUBFASES_OPTIONS} />
              </Field>
              <Field label="Archivo PDF del documento" span={2}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input ref={fileInputRef} type="file" accept="application/pdf"
                    onChange={e => onSelectFile(e.target.files?.[0] || null)}
                    style={{ ...INPUT_STYLE, padding: '0.35rem 0.5rem', flex: 1 }} />
                </div>
                {file && (
                  <div style={{ fontSize: '0.72rem', color: C.olive, fontFamily: FF, marginTop: '0.3rem' }}>
                    ✓ {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
                    {filePages !== null && <> · {filePages} páginas</>}
                  </div>
                )}
                {filePages !== null && filePages > MAX_PDF_PAGES_PER_CHUNK && (
                  <div style={{ marginTop: '0.4rem', padding: '0.55rem 0.7rem', background: '#EBF3F8', border: `1px solid ${C.accent}`, borderRadius: '4px', fontSize: '0.75rem', color: C.text, fontFamily: FF, lineHeight: 1.5 }}>
                    <Sparkle size={13} style={{ verticalAlign: 'middle', marginRight: '0.3rem' }} color={C.accent} />
                    Este PDF se troceará automáticamente en {Math.ceil(filePages / MAX_PDF_PAGES_PER_CHUNK)} partes de {MAX_PDF_PAGES_PER_CHUNK} páginas para procesarlo con Claude. En el expediente aparecerá como un único documento y el análisis se hará por rondas con contexto acumulado.
                  </div>
                )}
              </Field>
            </div>
            <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#EBF3F8', border: `1px solid ${C.accent}`, borderRadius: '4px', fontSize: '0.78rem', color: C.text, fontFamily: FF, lineHeight: 1.5 }}>
              <strong>Análisis IA:</strong> tras subir el documento aparecerá un botón «Analizar con IA» en el listado. Claude Haiku 4.5 extraerá sistemas · partidas · certificaciones · hallazgos según el tipo del documento, con cita literal + página exacta del PDF.
            </div>
            <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <Button variant="ghost" small onClick={() => { reset(); setShowForm(false) }} disabled={uploading}>Cancelar</Button>
              <Button small icon={uploading ? undefined : UploadSimple} onClick={guardar} disabled={uploading}>
                {uploading ? 'Subiendo PDF...' : 'Subir y guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showPicker && (
        <PickerDelPliego
          projectId={projectId}
          documentos={documentos}
          onCancel={() => setShowPicker(false)}
          onConfirm={async (payload) => {
            await onAddDesdePliego(payload)
            setShowPicker(false)
          }}
        />
      )}
    </Card>
  )
}

// ─── Modal · Elegir del pliego ─────────────────────────────────────────────
// Muestra los documentos ya subidos al pliego del proyecto y permite
// importarlos al cockpit como referencia (sin duplicar el archivo). El
// usuario elige el tipo del cockpit, un nombre visible y opcionalmente
// autor/fecha/subfase.
function PickerDelPliego({ projectId, documentos, onCancel, onConfirm }) {
  const [pliegoDocs, setPliegoDocs] = useState(null)
  const [q, setQ] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [tipo, setTipo] = useState('presupuesto')
  const [tipoOtro, setTipoOtro] = useState('')
  const [nombre, setNombre] = useState('')
  const [autor, setAutor] = useState('')
  const [fechaDoc, setFechaDoc] = useState('')
  const [subfase, setSubfase] = useState('')
  const [importing, setImporting] = useState(false)

  useEffect(() => {
    if (!projectId) return
    supabase.from('pliego_documentos')
      .select('id, filename, storage_path, size_bytes, uploaded_at, pliego_requisito_id')
      .eq('project_id', projectId)
      .order('uploaded_at', { ascending: false })
      .limit(500)
      .then(async ({ data }) => {
        // Enriquecer con ficha (criteria_code + rama) para mostrar al usuario
        const reqIds = Array.from(new Set((data || []).map(d => d.pliego_requisito_id).filter(Boolean)))
        let reqMap = {}
        if (reqIds.length) {
          const { data: reqs } = await supabase.from('pliego_requisitos')
            .select('id, criteria_code, criteria_name, rama')
            .in('id', reqIds)
          for (const r of (reqs || [])) reqMap[r.id] = r
        }
        setPliegoDocs((data || []).map(d => ({ ...d, ficha: reqMap[d.pliego_requisito_id] || null })))
      })
  }, [projectId])

  // IDs ya importados para no permitir duplicados
  const yaImportados = useMemo(() => {
    const s = new Set()
    for (const d of (documentos || [])) if (d.pliego_documento_id) s.add(d.pliego_documento_id)
    return s
  }, [documentos])

  const nq = (q || '').toLowerCase().trim()
  const filtered = (pliegoDocs || []).filter(d => {
    if (yaImportados.has(d.id)) return false
    if (!nq) return true
    return (
      (d.filename || '').toLowerCase().includes(nq) ||
      (d.ficha?.criteria_code || '').toLowerCase().includes(nq) ||
      (d.ficha?.criteria_name || '').toLowerCase().includes(nq)
    )
  })

  const seleccionado = filtered.find(d => d.id === selectedId) || null
  const puedeConfirmar = !!seleccionado && !!tipo && !importing

  const confirmar = async () => {
    if (!puedeConfirmar) return
    setImporting(true)
    try {
      await onConfirm({
        pliegoDoc: seleccionado,
        tipo,
        tipoOtro: tipo === 'otro' ? tipoOtro : null,
        nombre: nombre.trim() || seleccionado.filename,
        autor: autor.trim() || null,
        fechaDocumento: fechaDoc || seleccionado.uploaded_at?.slice(0, 10) || null,
        subfase: subfase.trim() || null,
      })
    } finally {
      setImporting(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(59,47,47,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }} onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      <div style={{
        background: 'white', borderRadius: 8, padding: '1.5rem',
        width: '90%', maxWidth: 720, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', gap: '1rem',
      }}>
        <div>
          <h3 style={{ margin: 0, fontFamily: FF, color: C.text, fontSize: '1rem' }}>
            Importar documento del pliego
          </h3>
          <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: C.muted, fontFamily: FF }}>
            Selecciona un documento ya subido al pliego del proyecto. Se referencia el mismo archivo · sin duplicar en storage.
          </p>
        </div>

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.35rem 0.65rem',
          border: `1px solid ${q ? C.olive : C.border}`,
          borderRadius: 4, background: 'white', maxWidth: 380,
        }}>
          <MagnifyingGlass size={13} color={q ? C.olive : C.muted} />
          <input value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Buscar por archivo o ficha…"
                 style={{
                   flex: 1, border: 'none', outline: 'none',
                   fontSize: '0.8rem', color: C.text, background: 'transparent',
                   fontFamily: FF,
                 }} />
        </div>

        <div style={{
          flex: 1, overflowY: 'auto', border: `1px solid ${C.border}`,
          borderRadius: 4, minHeight: 180, maxHeight: 260,
        }}>
          {pliegoDocs === null ? (
            <div style={{ padding: '1rem', color: C.muted, fontSize: '0.82rem', fontFamily: FF, fontStyle: 'italic' }}>
              Cargando documentos del pliego…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '1rem', color: C.muted, fontSize: '0.82rem', fontFamily: FF, fontStyle: 'italic' }}>
              {yaImportados.size > 0 && !q
                ? 'Todos los documentos del pliego ya están importados en el cockpit.'
                : 'Ningún documento coincide.'}
            </div>
          ) : (
            filtered.map(d => {
              const sel = d.id === selectedId
              return (
                <div key={d.id}
                     onClick={() => { setSelectedId(d.id); if (!nombre) setNombre(d.filename.replace(/\.pdf$/i, '')) }}
                     style={{
                       padding: '0.55rem 0.75rem',
                       borderBottom: `1px solid ${C.border}`,
                       cursor: 'pointer',
                       background: sel ? '#EBF3F8' : 'white',
                       borderLeft: sel ? `3px solid ${C.accent}` : '3px solid transparent',
                     }}>
                  <div style={{ fontSize: '0.82rem', color: C.text, fontFamily: FF, fontWeight: sel ? 600 : 500 }}>
                    {d.filename}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: 2, fontFamily: FF }}>
                    {d.ficha?.criteria_code ? `${d.ficha.criteria_code} · ${d.ficha.rama || '—'}` : '—'}
                    {' · '}
                    {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('es-ES') : ''}
                  </div>
                </div>
              )
            })
          )}
        </div>

        {seleccionado && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Field label="Tipo de documento (cockpit)" span={2}>
              <SelectInput value={tipo} onChange={setTipo} options={TIPOS_DOCUMENTO} />
            </Field>
            {tipo === 'otro' && (
              <Field label="Especificar tipo" span={2}>
                <TextInput value={tipoOtro} onChange={setTipoOtro} placeholder="Ej. Estudio geotécnico" />
              </Field>
            )}
            <Field label="Nombre visible" span={2}>
              <TextInput value={nombre} onChange={setNombre} placeholder="Nombre en el cockpit" />
            </Field>
            <Field label="Autor / redactor">
              <TextInput value={autor} onChange={setAutor} placeholder="Opcional" />
            </Field>
            <Field label="Fecha del documento">
              <TextInput value={fechaDoc} onChange={setFechaDoc} type="date" />
            </Field>
            <Field label="Subfase" span={2}>
              <TextInput value={subfase} onChange={setSubfase} placeholder="Opcional · ej. 1.2 y 1.3" />
            </Field>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <Button variant="ghost" small onClick={onCancel} disabled={importing}>Cancelar</Button>
          <Button small onClick={confirmar} disabled={!puedeConfirmar}>
            {importing ? 'Importando…' : 'Importar al cockpit'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function DocumentoRow({ d, onDelete, onAnalyze, onView, nested = false }) {
  const [busy, setBusy] = useState(false)
  const tipoLabel = TIPO_LABELS[d.tipo] || d.tipo
  const estado = ESTADO_LABELS[d.analisis_estado] || ESTADO_LABELS.pendiente
  const analizado = d.analisis_estado === 'completado'
  const puedeAnalizar = d.analisis_estado !== 'en_curso'
  const nHallazgos = (d.hallazgos_extraidos?.length || 0) + (d.partidas_detectadas?.length || 0) + (d.sistemas_constructivos?.length || 0)
  const nChunks = Array.isArray(d.chunks) ? d.chunks.length : 0
  const enCurso = d.analisis_estado === 'en_curso'

  const analizar = async () => {
    setBusy(true)
    try { await onAnalyze(d.id) } finally { setBusy(false) }
  }

  return (
    <div style={{
      padding: nested ? '0.55rem 0.7rem' : '0.75rem',
      border: `1px solid ${analizado ? C.olive : C.border}`,
      borderRadius: '4px', background: nested ? 'white' : '#FDFCF9',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{
          padding: '0.28rem 0.65rem', background: '#EBF3F8', color: C.accent,
          borderRadius: '3px', fontSize: '0.7rem', fontWeight: '600',
          letterSpacing: '0.3px', fontFamily: FF, whiteSpace: 'nowrap',
        }}>
          {tipoLabel}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FF, fontWeight: '600', color: C.text, fontSize: '0.88rem' }}>
            {d.nombre}
          </div>
          <div style={{ fontSize: '0.72rem', color: C.muted, fontFamily: FF }}>
            {d.autor_asesor && <>{d.autor_asesor} · </>}
            {d.fecha_documento || 'Sin fecha'}
            {d.archivo_bytes && <> · {(d.archivo_bytes / 1024 / 1024).toFixed(1)} MB</>}
            {d.archivo_paginas && <> · {d.archivo_paginas} páginas</>}
            {nChunks > 1 && <> · {nChunks} rondas</>}
          </div>
        </div>
        <div style={{
          padding: '0.28rem 0.65rem', background: estado.bg, color: estado.color,
          borderRadius: '3px', fontSize: '0.72rem', fontWeight: '600', fontFamily: FF, whiteSpace: 'nowrap',
        }}>
          {enCurso && d.analisis_ronda_actual && d.analisis_rondas_totales
            ? `Ronda ${d.analisis_ronda_actual}/${d.analisis_rondas_totales}`
            : estado.label}
        </div>
        {analizado && (
          <Button variant="ghost" small icon={Eye} onClick={() => onView(d)}>
            Ver ({nHallazgos})
          </Button>
        )}
        {puedeAnalizar && (
          <Button variant={analizado ? 'ghost' : 'olive'} small
            icon={busy ? ArrowClockwise : (analizado ? ArrowClockwise : Sparkle)}
            onClick={analizar} disabled={busy}>
            {busy ? 'Analizando...' : (analizado ? 'Reanalizar' : 'Analizar con IA')}
          </Button>
        )}
        <Button variant="ghost" small icon={TrashSimple}
          onClick={() => { if (window.confirm(`¿Eliminar «${d.nombre}»?`)) onDelete(d) }}>
          Eliminar
        </Button>
      </div>
      {d.analisis_estado === 'error' && d.analisis_error && (
        <div style={{
          marginTop: '0.5rem', padding: '0.4rem 0.6rem',
          background: '#FDECE7', border: `1px solid ${C.warn}`, borderRadius: '3px',
          fontSize: '0.72rem', color: C.warn, fontFamily: FF,
        }}>
          <Warning size={12} style={{ verticalAlign: 'middle', marginRight: '0.3rem' }} />
          {d.analisis_error}
        </div>
      )}
      {analizado && d.resumen_ia && (
        <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: C.text, fontStyle: 'italic', fontFamily: FF, lineHeight: 1.5 }}>
          <Sparkle size={12} color={C.olive} style={{ verticalAlign: 'middle', marginRight: '0.3rem' }} />
          {d.resumen_ia}
        </div>
      )}
    </div>
  )
}

// ─── Modal de detalle · muestra hallazgos, partidas o sistemas ─────────────
function DetalleDocumento({ doc, onClose }) {
  if (!doc) return null
  const hallazgos = doc.hallazgos_extraidos || []
  const partidas = doc.partidas_detectadas || []
  const sistemas = doc.sistemas_constructivos || []

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(59,47,47,0.5)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{
        background: 'white', borderRadius: '8px',
        width: '90%', maxWidth: '900px', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: '1rem 1.5rem', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: '0.75rem',
        }}>
          <Sparkle size={22} color={C.olive} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: FF, fontWeight: '600', color: C.text, fontSize: '1rem' }}>
              Extracción IA · {doc.nombre}
            </div>
            <div style={{ fontSize: '0.78rem', color: C.muted, fontFamily: FF }}>
              {TIPO_LABELS[doc.tipo]} · {doc.analisis_modelo || 'claude-haiku-4-5'}
              {doc.analisis_fecha && ' · ' + new Date(doc.analisis_fecha).toLocaleString('es-ES')}
            </div>
          </div>
          <Button variant="ghost" small onClick={onClose}>Cerrar</Button>
        </div>
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          {doc.resumen_ia && (
            <div style={{ padding: '0.75rem 1rem', background: '#F5F0EA', borderRadius: '4px', marginBottom: '1rem', fontFamily: FF, fontSize: '0.86rem', color: C.text, lineHeight: 1.6 }}>
              <div style={{ fontSize: '0.72rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.35rem', fontWeight: '600' }}>Resumen</div>
              {doc.resumen_ia}
            </div>
          )}

          {sistemas.length > 0 && (
            <>
              <SectionTitle icon={Folders}>Sistemas constructivos identificados ({sistemas.length})</SectionTitle>
              <div style={{ display: 'grid', gap: '0.6rem', marginBottom: '1rem' }}>
                {sistemas.map((s, i) => <SistemaCard key={i} s={s} />)}
              </div>
            </>
          )}

          {partidas.length > 0 && (
            <>
              <SectionTitle icon={Folders}>Partidas del presupuesto ({partidas.length})</SectionTitle>
              <div style={{ display: 'grid', gap: '0.6rem', marginBottom: '1rem' }}>
                {partidas.map((p, i) => <PartidaCard key={i} p={p} />)}
              </div>
            </>
          )}

          {hallazgos.length > 0 && (
            <>
              <SectionTitle icon={Sparkle}>Hallazgos ({hallazgos.length})</SectionTitle>
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {hallazgos.map((h, i) => <HallazgoCard key={i} h={h} />)}
              </div>
            </>
          )}

          {!sistemas.length && !partidas.length && !hallazgos.length && (
            <EmptyState icon={Sparkle} title="La IA no encontró hallazgos"
              hint="El documento se procesó correctamente pero no se detectaron elementos relevantes para BREEAM. Puedes reanalizar o revisar el PDF original." />
          )}
        </div>
      </div>
    </div>
  )
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.4rem',
      marginBottom: '0.6rem', paddingBottom: '0.4rem',
      borderBottom: `1px solid ${C.border}`,
    }}>
      <Icon size={14} color={C.accent} />
      <span style={{ fontFamily: FF, fontWeight: '600', color: C.text, fontSize: '0.86rem' }}>
        {children}
      </span>
    </div>
  )
}

function CriteriaPills({ criterios }) {
  if (!criterios?.length) return null
  return (
    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
      {criterios.map((c, i) => (
        <span key={i} style={{
          padding: '0.15rem 0.45rem', background: '#E8F1D8', color: C.olive,
          borderRadius: '2px', fontSize: '0.68rem', fontWeight: '600', fontFamily: FF,
        }}>{c}</span>
      ))}
    </div>
  )
}

function SistemaCard({ s }) {
  return (
    <div style={{ padding: '0.7rem 0.85rem', border: `1px solid ${C.border}`, borderRadius: '4px', background: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem' }}>
        <span style={{
          padding: '0.15rem 0.45rem', background: '#EBF3F8', color: C.accent,
          borderRadius: '2px', fontSize: '0.68rem', fontWeight: '600', fontFamily: FF, textTransform: 'uppercase',
        }}>{s.categoria}</span>
        {s.pagina && (
          <span style={{ fontSize: '0.7rem', color: C.muted, fontFamily: FF }}>
            página {s.pagina}
          </span>
        )}
        {s._parte && (
          <span style={{ fontSize: '0.68rem', color: C.olive, fontFamily: FF, fontWeight: '600' }}>
            · parte {s._parte}
          </span>
        )}
      </div>
      <div style={{ fontFamily: FF, fontSize: '0.84rem', color: C.text, lineHeight: 1.5 }}>
        {s.descripcion}
      </div>
      {s.cita_literal && (
        <div style={{ padding: '0.4rem 0.6rem', marginTop: '0.5rem', background: '#FDFCF9', borderLeft: `2px solid ${C.olive}`, fontSize: '0.76rem', color: C.text, fontStyle: 'italic', fontFamily: FF, lineHeight: 1.55 }}>
          «{s.cita_literal}»
        </div>
      )}
      {s.materiales_mencionados?.length > 0 && (
        <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: C.muted, fontFamily: FF }}>
          <strong style={{ color: C.text }}>Materiales:</strong> {s.materiales_mencionados.join(' · ')}
        </div>
      )}
      {s.certificaciones_mencionadas?.length > 0 && (
        <div style={{ marginTop: '0.15rem', fontSize: '0.75rem', color: C.muted, fontFamily: FF }}>
          <strong style={{ color: C.text }}>Certificaciones:</strong> {s.certificaciones_mencionadas.join(' · ')}
        </div>
      )}
      <CriteriaPills criterios={s.criterios_breeam_relacionados} />
    </div>
  )
}

function PartidaCard({ p }) {
  return (
    <div style={{ padding: '0.7rem 0.85rem', border: `1px solid ${C.border}`, borderRadius: '4px', background: 'white' }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
        {p.codigo && (
          <span style={{ fontFamily: FF, fontWeight: '600', color: C.accent, fontSize: '0.8rem' }}>
            {p.codigo}
          </span>
        )}
        <div style={{ flex: 1, fontFamily: FF, color: C.text, fontSize: '0.84rem', lineHeight: 1.4 }}>
          {p.nombre_partida}
        </div>
        {p.pagina && (
          <span style={{ fontSize: '0.7rem', color: C.muted, fontFamily: FF }}>
            pág. {p.pagina}
          </span>
        )}
        {p._parte && (
          <span style={{ fontSize: '0.68rem', color: C.olive, fontFamily: FF, fontWeight: '600' }}>
            parte {p._parte}
          </span>
        )}
      </div>
      {p.materiales?.length > 0 && (
        <div style={{ marginTop: '0.5rem', paddingTop: '0.4rem', borderTop: `1px dashed ${C.border}` }}>
          {p.materiales.map((m, i) => (
            <div key={i} style={{
              display: 'flex', gap: '0.5rem', padding: '0.3rem 0',
              fontSize: '0.78rem', fontFamily: FF, color: C.text, alignItems: 'baseline',
            }}>
              <span style={{ flex: 1 }}>{m.nombre}</span>
              {m.cantidad !== null && m.cantidad !== undefined && (
                <span style={{ color: C.muted }}>{m.cantidad} {m.unidad || ''}</span>
              )}
              {m.elemento_breeam && (
                <span style={{
                  padding: '0.1rem 0.35rem', background: '#F5F0EA',
                  color: C.text, borderRadius: '2px', fontSize: '0.68rem', fontFamily: FF,
                }}>{m.elemento_breeam}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function HallazgoCard({ h }) {
  const confColor = h.confianza === 'alta' ? C.olive : h.confianza === 'media' ? C.amber : C.muted
  return (
    <div style={{ padding: '0.7rem 0.85rem', border: `1px solid ${C.border}`, borderRadius: '4px', background: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem' }}>
        <span style={{
          padding: '0.13rem 0.4rem', background: '#F5F0EA', color: C.text,
          borderRadius: '2px', fontSize: '0.68rem', fontWeight: '600', fontFamily: FF, textTransform: 'uppercase',
        }}>{h.tema}</span>
        {h.confianza && (
          <span style={{ fontSize: '0.68rem', color: confColor, fontFamily: FF, fontWeight: '600' }}>
            · confianza {h.confianza}
          </span>
        )}
        {h.pagina && (
          <span style={{ fontSize: '0.7rem', color: C.muted, fontFamily: FF }}>
            · pág. {h.pagina}
          </span>
        )}
        {h._parte && (
          <span style={{ fontSize: '0.68rem', color: C.olive, fontFamily: FF, fontWeight: '600' }}>
            · parte {h._parte}
          </span>
        )}
      </div>
      {h.criterio_breeam && (
        <div style={{ fontFamily: FF, fontWeight: '600', color: C.accent, fontSize: '0.82rem', marginBottom: '0.15rem' }}>
          {h.criterio_breeam}{h.puntos ? ` · ${h.puntos} pt${h.puntos > 1 ? 's' : ''}` : ''}
          {h.frecuencia ? ` · ${h.frecuencia}` : ''}
        </div>
      )}
      <div style={{ fontFamily: FF, fontSize: '0.82rem', color: C.text, lineHeight: 1.5 }}>
        {h.hallazgo || h.requisito}
      </div>
      {h.cita_literal && (
        <div style={{ padding: '0.4rem 0.6rem', marginTop: '0.4rem', background: '#FDFCF9', borderLeft: `2px solid ${C.olive}`, fontSize: '0.76rem', color: C.text, fontStyle: 'italic', fontFamily: FF, lineHeight: 1.55 }}>
          «{h.cita_literal}»
        </div>
      )}
      <CriteriaPills criterios={h.criterios_breeam_relacionados} />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//   COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════
export default function CockpitContratista({ projectId, projectName }) {
  const [tab, setTab] = useState('setup')
  const [expediente, setExpediente] = useState(null)
  const [contratistas, setContratistas] = useState([])
  const [documentos, setDocumentos] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [userId, setUserId] = useState(null)
  const [detalleDoc, setDetalleDoc] = useState(null)

  // Carga inicial: expediente + contratistas + documentos del proyecto
  const cargar = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }
    setUserId(user.id)

    // Expediente (uno por proyecto · UNIQUE)
    const { data: expData } = await supabase.from('cc_expedientes')
      .select('*').eq('project_id', projectId).maybeSingle()
    setExpediente(expData)

    // Contratistas del proyecto
    const { data: cData } = await supabase.from('cc_contratistas')
      .select('*').eq('project_id', projectId).eq('activo', true)
      .order('rol', { ascending: true }).order('created_at', { ascending: true })
    setContratistas(cData || [])

    // Documentos (solo si hay expediente)
    if (expData?.id) {
      const { data: dData } = await supabase.from('cc_documentos_proyecto')
        .select('*').eq('expediente_id', expData.id)
        .order('created_at', { ascending: false })
      setDocumentos(dData || [])
    }

    setLoading(false)
  }, [projectId])

  useEffect(() => { cargar() }, [cargar])

  // Guardar expediente (crear o actualizar)
  const guardarExpediente = async (form) => {
    if (!userId || !projectId) return
    setSaving(true)
    // Excluimos las columnas GENERATED ALWAYS (calculadas por la BD) del
    // payload de UPDATE. objetivo_pct se calcula automáticamente desde
    // nivel_objetivo por la migración 20260826, y Postgres rechaza el UPDATE
    // completo si el payload la incluye ("column can only be updated to DEFAULT").
    // eslint-disable-next-line no-unused-vars
    const { objetivo_pct, id, created_at, updated_at, ...formLimpio } = form
    const payload = {
      ...formLimpio,
      duracion_meses: formLimpio.duracion_meses ? parseInt(formLimpio.duracion_meses, 10) : null,
      fecha_inicio_obra: formLimpio.fecha_inicio_obra || null,
      user_id: userId,
      project_id: projectId,
    }
    let result
    if (expediente?.id) {
      result = await supabase.from('cc_expedientes').update(payload).eq('id', expediente.id).select().maybeSingle()
    } else {
      result = await supabase.from('cc_expedientes').insert(payload).select().maybeSingle()
    }
    setSaving(false)
    if (result.error) { alert(`Error al guardar el expediente · ${result.error.message}`); return }
    setExpediente(result.data)
  }

  // Contratistas
  const addContratista = async (c) => {
    if (!userId || !projectId) return
    const { data, error } = await supabase.from('cc_contratistas')
      .insert({ ...c, user_id: userId, project_id: projectId }).select().maybeSingle()
    if (error) { alert(`Error · ${error.message}`); return }
    setContratistas(cs => [...cs, data])
  }
  const delContratista = async (id) => {
    const { error } = await supabase.from('cc_contratistas').delete().eq('id', id)
    if (error) { alert(`Error · ${error.message}`); return }
    setContratistas(cs => cs.filter(c => c.id !== id))
  }
  const updateContratista = async (id, patch) => {
    const { data, error } = await supabase.from('cc_contratistas').update(patch).eq('id', id).select().maybeSingle()
    if (error) { alert(`Error · ${error.message}`); return }
    setContratistas(cs => cs.map(c => c.id === id ? data : c))
  }

  // Documentos del proyecto
  const addDocumento = async (d) => {
    if (!userId || !projectId || !expediente?.id) return
    // La columna 'subfase' no existe en el esquema; guardamos el metadato en
    // notas_extraccion como JSON para poder recuperarlo al analizar.
    const { subfase, ...docWithoutSubfase } = d
    const payload = {
      ...docWithoutSubfase,
      notas_extraccion: subfase ? JSON.stringify({ subfase }) : (d.notas_extraccion || null),
      fecha_documento: d.fecha_documento || null,
      user_id: userId,
      project_id: projectId,
      expediente_id: expediente.id,
      // storage_path viene ya del componente hijo tras subir el PDF
    }
    const { data, error } = await supabase.from('cc_documentos_proyecto').insert(payload).select().maybeSingle()
    if (error) {
      // Si falla el insert, borra el PDF ya subido para no dejar huérfano
      if (d.storage_path) {
        await supabase.storage.from('cc-documentos-proyecto').remove([d.storage_path])
      }
      alert(`Error al registrar el documento · ${error.message}`)
      return
    }
    setDocumentos(ds => [data, ...ds])
  }
  const delDocumento = async (d) => {
    // Si es una referencia al pliego (importado con "Elegir del pliego"), NO
    // borramos el archivo físico · pertenece al pliego, no al cockpit. Solo
    // se borra la fila de cc_documentos_proyecto.
    const esReferenciaPliego = !!d.pliego_documento_id
    if (!esReferenciaPliego) {
      // Borrar del Storage: puede ser un PDF único o un array de chunks
      const paths = []
      if (d.storage_path && d.storage_path !== 'pendiente_fase_2') paths.push(d.storage_path)
      if (Array.isArray(d.chunks)) {
        for (const c of d.chunks) if (c.storage_path) paths.push(c.storage_path)
      }
      if (paths.length > 0) {
        await supabase.storage.from('cc-documentos-proyecto').remove(paths)
      }
    }
    const { error } = await supabase.from('cc_documentos_proyecto').delete().eq('id', d.id)
    if (error) { alert(`Error · ${error.message}`); return }
    setDocumentos(ds => ds.filter(x => x.id !== d.id))
  }

  // Importar un documento ya subido al pliego como referencia en el cockpit.
  // No duplica el archivo · ambas filas apuntan al mismo storage_path en el
  // bucket `evidences`. La fila del cockpit lleva pliego_documento_id para
  // que delDocumento sepa no borrar del storage.
  const addDocumentoDesdePliego = async ({ pliegoDoc, tipo, tipoOtro, nombre, autor, fechaDocumento, subfase }) => {
    if (!userId || !projectId || !expediente?.id) return
    if (!pliegoDoc?.storage_path) { alert('El documento del pliego no tiene archivo asociado'); return }
    const payload = {
      user_id: userId,
      project_id: projectId,
      expediente_id: expediente.id,
      tipo,
      tipo_otro: tipoOtro || null,
      nombre: nombre || pliegoDoc.filename,
      autor_asesor: autor || null,
      fecha_documento: fechaDocumento || null,
      storage_path: pliegoDoc.storage_path,
      archivo_bytes: pliegoDoc.size_bytes || null,
      pliego_documento_id: pliegoDoc.id,
      analisis_estado: 'pendiente',
      notas_extraccion: subfase ? JSON.stringify({ subfase }) : null,
    }
    const { data, error } = await supabase.from('cc_documentos_proyecto')
      .insert(payload).select().maybeSingle()
    if (error) { alert(`Error al importar · ${error.message}`); return }
    setDocumentos(ds => [data, ...ds])
  }
  const analizarDocumento = async (documentoId) => {
    // Orquestador de rondas usando la Edge Function GENÉRICA breeam-analyzer
    // (misma que usa el módulo de Criterios BREEAM) · patrón probado en
    // producción con streaming SSE + parser JSON robusto compartido.
    //
    // Por cada chunk del documento:
    //   1. Genera signed URL del PDF en el bucket cc-documentos-proyecto
    //   2. Llama a breeam-analyzer con el prompt del tipo + contexto acumulado
    //   3. Parsea la respuesta con parseAiJson (compartido con App.js)
    //   4. Acumula sistemas, partidas, hallazgos y resúmenes
    // Al terminar, PATCH único con el resultado consolidado.

    setDocumentos(ds => ds.map(x =>
      x.id === documentoId ? { ...x, analisis_estado: 'en_curso', analisis_error: null, analisis_ronda_actual: 1 } : x
    ))

    const acumulado = { sistemas: [], partidas: [], hallazgos: [], resumenes: [] }
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim()
    const functionUrl = `${supabaseUrl}/functions/v1/breeam-analyzer`

    try {
      // Cargar el documento con sus chunks
      const { data: doc, error: docErr } = await supabase
        .from('cc_documentos_proyecto')
        .select('*').eq('id', documentoId).maybeSingle()
      if (docErr || !doc) throw new Error(docErr?.message || 'Documento no encontrado')

      // Determinar el conjunto de "rondas" (chunks o storage_path único)
      const chunks = Array.isArray(doc.chunks) ? doc.chunks : []
      const rondas = chunks.length > 0
        ? chunks.slice().sort((a, b) => (a.indice || 0) - (b.indice || 0))
        : (doc.storage_path ? [{ storage_path: doc.storage_path, indice: 0 }] : [])
      if (rondas.length === 0) throw new Error('El documento no tiene PDF subido')
      const totalRondas = rondas.length

      // Marcar rondas totales en la BD
      await supabase.from('cc_documentos_proyecto').update({
        analisis_rondas_totales: totalRondas,
        analisis_ronda_actual: 1,
        analisis_estado: 'en_curso',
        analisis_error: null,
      }).eq('id', documentoId)

      for (let rondaIndex = 0; rondaIndex < totalRondas; rondaIndex++) {
        const chunk = rondas[rondaIndex]
        const rondaNum = rondaIndex + 1

        // Actualizar UI local + BD
        setDocumentos(ds => ds.map(x =>
          x.id === documentoId
            ? { ...x, analisis_ronda_actual: rondaNum, analisis_rondas_totales: totalRondas }
            : x
        ))
        if (rondaIndex > 0) {
          await supabase.from('cc_documentos_proyecto')
            .update({ analisis_ronda_actual: rondaNum })
            .eq('id', documentoId)
        }

        // 1. Signed URL del chunk (validez 3600s por seguridad).
        // Si el documento fue importado del pliego, el archivo vive en el bucket
        // `evidences` (no en cc-documentos-proyecto).
        const bucket = doc.pliego_documento_id ? 'evidences' : 'cc-documentos-proyecto'
        const { data: signed, error: sErr } = await supabase.storage
          .from(bucket)
          .createSignedUrl(chunk.storage_path, 3600)
        if (sErr || !signed?.signedUrl) {
          throw new Error(`Ronda ${rondaNum}: no se pudo firmar la URL del chunk (${sErr?.message || 'sin URL'})`)
        }

        // 2. Prompt del tipo + contexto acumulado + subfase del documento
        // La subfase la guardamos en notas_extraccion como JSON: {"subfase":"A2"}
        let docSubfase = null
        try { docSubfase = JSON.parse(doc.notas_extraccion || 'null')?.subfase || null } catch {}
        const prompt = buildPromptDocumento(doc.tipo, doc.nombre, rondaIndex, totalRondas, acumulado, docSubfase)

        // 3. Invocar breeam-analyzer (helper compartido con streaming SSE)
        const nombreFichero = `${doc.nombre} · parte ${rondaNum}de${totalRondas}.pdf`
        let apiData
        try {
          apiData = await callBreeamAnalyzer(functionUrl, anonKey, {
            file_infos: [{ name: nombreFichero, signed_url: signed.signedUrl, media_type: 'application/pdf' }],
            direct_files: [],
            prompt_text: prompt,
            model: 'claude-haiku-4-5-20251001',
            max_tokens: 16000,
          })
        } catch (rondaErr) {
          // Si es STREAM_ERROR seguimos con la siguiente ronda (patrón App.js)
          if (rondaErr.message?.startsWith('STREAM_ERROR:') && totalRondas > 1) {
            console.warn(`[Cockpit] Ronda ${rondaNum}/${totalRondas} omitida por stream:`, rondaErr.message)
            continue
          }
          throw rondaErr
        }
        if (apiData?.error) throw new Error(`Ronda ${rondaNum}: ${apiData.error}`)

        // 4. Parsear con el parser robusto compartido
        const rawText = apiData.content?.map(c => c.text || '').join('') || ''
        let parsed
        try {
          parsed = parseAiJson(rawText)
        } catch (parseErr) {
          console.error(`[Cockpit] Error parseando JSON en ronda ${rondaNum}:`, parseErr.message)
          console.error('[Cockpit] Texto recibido (primeros 2000 chars):', rawText.substring(0, 2000))
          console.error('[Cockpit] Texto recibido (últimos 500 chars):', rawText.substring(Math.max(0, rawText.length - 500)))
          throw new Error(`Ronda ${rondaNum}: ${parseErr.message}`)
        }

        // 5. Acumular
        if (Array.isArray(parsed.sistemas))  acumulado.sistemas.push(...parsed.sistemas)
        if (Array.isArray(parsed.partidas))  acumulado.partidas.push(...parsed.partidas)
        if (Array.isArray(parsed.hallazgos)) acumulado.hallazgos.push(...parsed.hallazgos)
        if (parsed.resumen) acumulado.resumenes.push(parsed.resumen)
      }

      // Persistir resultado consolidado
      const update = {
        analisis_estado: 'completado',
        analisis_fecha: new Date().toISOString(),
        analisis_modelo: 'claude-haiku-4-5-20251001',
        analisis_error: null,
        analisis_ronda_actual: null,
        analizado_por_ia: true,
        resumen_ia: acumulado.resumenes.length > 1
          ? acumulado.resumenes.join(' | ')
          : (acumulado.resumenes[0] || null),
        hallazgos_extraidos: acumulado.hallazgos,
        partidas_detectadas: acumulado.partidas.length > 0 ? acumulado.partidas : null,
        sistemas_constructivos: acumulado.sistemas.length > 0 ? acumulado.sistemas : null,
      }
      const { data: refreshed, error: patchErr } = await supabase
        .from('cc_documentos_proyecto')
        .update(update).eq('id', documentoId).select().maybeSingle()
      if (patchErr) throw new Error(`Error al guardar el resultado · ${patchErr.message}`)
      if (refreshed) {
        setDocumentos(ds => ds.map(x => x.id === documentoId ? refreshed : x))
      }

      // Snapshot de trayectoria: cada vez que un documento se analiza con éxito,
      // dispara una re-evaluación de la trayectoria del expediente. El RPC
      // calcula la puntuación acumulada y guarda un snapshot que la tab
      // Trayectoria consume para dibujar la curva.
      if (expediente?.id) {
        try {
          const docLabel = refreshed?.nombre_original
            ? `Análisis IA · ${refreshed.nombre_original}`
            : 'Análisis IA de documento'
          await supabase.rpc('cc_snapshot_trayectoria', {
            p_expediente_id: expediente.id,
            p_evento_tipo:   'documento_analizado',
            p_evento_ref_id: documentoId,
            p_evento_desc:   docLabel,
          })
        } catch (snapErr) {
          // No romper el flujo si el snapshot falla — es telemetría, no bloqueante
          console.warn('[Cockpit] Snapshot de trayectoria falló:', snapErr?.message || snapErr)
        }
      }
    } catch (e) {
      const msg = e.message || 'Error desconocido'
      setDocumentos(ds => ds.map(x =>
        x.id === documentoId ? { ...x, analisis_estado: 'error', analisis_error: msg, analisis_ronda_actual: null } : x
      ))
      await supabase.from('cc_documentos_proyecto').update({
        analisis_estado: 'error', analisis_error: msg.substring(0, 500), analisis_ronda_actual: null,
      }).eq('id', documentoId)
      alert(`Error en el análisis · ${msg}`)
    }
  }

  if (!projectId) {
    return (
      <div style={{ padding: '2rem', fontFamily: FF, color: C.muted, textAlign: 'center' }}>
        Selecciona un proyecto para abrir el Cockpit del Contratista.
      </div>
    )
  }

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '1200px', fontFamily: FF, background: C.bg, minHeight: '100%' }}>
      {/* Cabecera · mismo estilo que ProjectDetails para coherencia */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        marginBottom: '1.25rem', paddingBottom: '0.875rem',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <HardHat size={26} color={C.accent} weight="duotone" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '1.15rem', fontWeight: '700', color: C.text }}>
            Cockpit del Contratista
          </div>
          <div style={{ fontSize: '0.78rem', color: C.muted, marginTop: '0.15rem' }}>
            {projectName ? `${projectName} · ` : ''}Preámbulo estructurado a la certificación
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: `1px solid ${C.border}` }}>
        {[
          { id: 'setup',        label: 'Setup del expediente', icon: GearSix },
          { id: 'contratistas', label: `Contratistas${contratistas.length ? ` (${contratistas.length})` : ''}`, icon: Users },
          { id: 'documentos',   label: `Documentación del proyecto${documentos.length ? ` (${documentos.length})` : ''}`, icon: FileText },
          // { id: 'planes',       label: 'Planes iniciales', icon: Folders }, // OCULTO 2026-09-25 · duplicaba trabajo con la rama Contratista del pliego. Componente y tabla cc_planes_iniciales se mantienen por si hay que recuperar.
          { id: 'seguimiento',  label: 'Seguimiento', icon: ChartLineUp },
          { id: 'trayectoria',  label: 'Trayectoria', icon: ChartLineUp },
          { id: 'proximos',     label: 'Próximas fases', icon: ArrowRight },
        ].map(t => {
          const active = tab === t.id
          const Icon = t.icon
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                padding: '0.65rem 1rem', background: 'transparent',
                border: 'none', borderBottom: active ? `2px solid ${C.accent}` : '2px solid transparent',
                color: active ? C.accent : C.muted,
                fontFamily: FF, fontSize: '0.86rem', fontWeight: active ? '600' : '500',
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              }}>
              <Icon size={15} /> {t.label}
            </button>
          )
        })}
      </div>

      {loading ? (
        <div style={{ padding: '2rem', color: C.muted, textAlign: 'center' }}>Cargando expediente...</div>
      ) : (
        <>
          {tab === 'setup' && (
            <SetupExpediente expediente={expediente} onSave={guardarExpediente} saving={saving} />
          )}
          {tab === 'contratistas' && (
            <TabContratistas contratistas={contratistas} onAdd={addContratista}
              onDelete={delContratista} onUpdate={updateContratista}
              expedienteId={expediente?.id} />
          )}
          {tab === 'documentos' && (
            <TabDocumentos
              documentos={documentos}
              onAdd={addDocumento}
              onAddDesdePliego={addDocumentoDesdePliego}
              onDelete={delDocumento}
              onAnalyze={analizarDocumento}
              onViewDetail={setDetalleDoc}
              expedienteId={expediente?.id}
              projectId={projectId}
              userId={userId}
            />
          )}
          {detalleDoc && <DetalleDocumento doc={detalleDoc} onClose={() => setDetalleDoc(null)} />}
          {tab === 'planes' && (
            <PlanesIniciales
              expedienteId={expediente?.id}
              projectId={projectId}
              projectName={projectName}
              userId={userId}
              contratistaPrincipal={contratistas.find(c => c.rol === 'principal')}
              documentos={documentos}
            />
          )}
          {tab === 'seguimiento' && (
            <Seguimiento
              expedienteId={expediente?.id}
              projectId={projectId}
              userId={userId}
              isAdmin={true}
            />
          )}
          {tab === 'trayectoria' && (
            <TrayectoriaCertificacion
              expediente={expediente}
              projectId={projectId}
            />
          )}
          {tab === 'proximos' && (
            <Card title="Próximas fases · roadmap del módulo" icon={Folders}>
              <div style={{ display: 'grid', gap: '0.65rem', fontSize: '0.86rem', color: C.text, lineHeight: 1.6 }}>
                <RoadmapItem fase="2" titulo="Extracción IA de documentos" done
                  descripcion="Fase 2 · operativa. La IA procesa memoria, presupuesto, pliegos y Scope of Works con prompt específico por tipo. Extrae sistemas constructivos, partidas, requisitos con cita literal + página del PDF fuente." />
                <RoadmapItem fase="3" titulo="Planes iniciales requeridos" done
                  descripcion="Sesión 1 operativa · editor estructurado del PPCAI (SYB 02) con los 6 apartados oficiales del pliego + 8 categorías de productos con umbrales COV/formaldehído + generador con IA a partir de documentos ya analizados. Próximas sesiones · PGR, Plan GST 03, Plan de Puesta en Servicio, GUE, cartas de compromiso." />
                <RoadmapItem fase="4" titulo="5 flujos de seguimiento continuo"
                  descripcion="Materiales · Consumos · Operarios · Residuos · Transportes. Configurables según lo que exija el pliego. Los materiales aprovechan las partidas ya extraídas en Fase 2." />
                <RoadmapItem fase="5" titulo="Dashboard + régimen A-E"
                  descripcion="Vista integrada del estado del expediente, alertas proactivas antes del cierre de mes, cálculo automático del nivel Correcto · Aceptable · Degradado." />
                <RoadmapItem fase="6" titulo="Auto-vinculación con criterios BREEAM"
                  descripcion="Cada hallazgo o evidencia queda enlazada al criterio BREEAM que sustenta. El módulo de 49 criterios muestra icono verde en cada criterio con evidencia disponible del contratista." />
                <RoadmapItem fase="7" titulo="Formaciones + actas de reunión"
                  descripcion="Registro digital de sesiones formativas con firma y foto. Actas periódicas con DF y asesor certificador." />
                <RoadmapItem fase="8" titulo="Piloto AIRES ABAMA"
                  descripcion="Crear el proyecto AIRES ABAMA y alimentar el expediente con los pliegos MAD2 · Global Switch para validar el flujo completo." />
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

function RoadmapItem({ fase, titulo, descripcion, done = false }) {
  const bg = done ? C.olive : C.accent
  return (
    <div style={{ display: 'flex', gap: '0.75rem', padding: '0.6rem 0', borderBottom: `1px dashed ${C.border}` }}>
      <div style={{
        minWidth: '34px', height: '34px', borderRadius: '50%',
        background: bg, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FF, fontWeight: '600', fontSize: '0.85rem',
      }}>
        {done ? <CheckCircle size={18} weight="fill" /> : fase}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: FF, fontWeight: '600', color: C.text, fontSize: '0.9rem' }}>
          {titulo} {done && <span style={{ color: C.olive, fontSize: '0.72rem', marginLeft: '0.4rem' }}>· operativa</span>}
        </div>
        <div style={{ fontSize: '0.8rem', color: C.muted, marginTop: '0.15rem' }}>{descripcion}</div>
      </div>
    </div>
  )
}
